import React from 'react'

import { CommonCardProps } from 'component-library/cards/CardItem.Props'

import { StyledText, StyledTimestamp } from 'component-library/cards/CardText/CardText.styled'
import { TimestampType } from 'component-library/content/CardTimestamp/CardTimestamp'
import { FontScales } from 'component-library/__styling/settings/fontScale'

import { ThemeMargins } from 'component-library/__styling/settings/metrics'
import { CardHeadline } from 'component-library/cards/CardText/CardHeadline'
import { CardTeaser } from 'component-library/cards/CardText/CardTeaser'
import { KickerPosition } from '../Kicker/Kicker.styled'
import { getLatestTime } from 'component-library/__helpers/get-latest-time'
import { MaybeLoaded } from 'json-react-layouts-data-loader'
import { CardHeadlineSkeleton } from './CardHeadlineSkeleton'
import { useTheme } from '@emotion/react'
import {
    PublicationCardItem,
    isPublicationCardItemWithVideo,
} from 'common/client-data-types/card-types'

export interface CardTextProps extends CommonCardProps {
    hasBackground?: boolean
    largeKicker?: boolean
    hasPadding?: boolean
    hidePremiumIcon?: boolean
    hideVideoIcon?: boolean
    timestamp?: TimestampType
    verticalSpacing?: keyof ThemeMargins
    kickerPosition?: KickerPosition
    isLarge?: boolean
    fontScale: FontScales
    innerRef?: (ref: any) => void

    item: MaybeLoaded<PublicationCardItem>
}

export const CardText: React.FC<CardTextProps> = props => {
    const {
        hideKicker = false,
        hideTeaser = false,
        largeKicker,
        fontScale,
        verticalSpacing,
        kickerPosition,
        hasBackground,
        hasPadding,
        hidePremiumIcon,
        hideVideoIcon,
        className,
        timestamp,
        isLarge,
        item,
        innerRef,
    } = props

    const theme = useTheme()
    const showTeaser = !hideTeaser

    return (
        <StyledText
            hasPadding={hasPadding}
            cardLoading={false}
            className={className}
            verticalSpacing={verticalSpacing}
            ref={innerRef}
            kickerPosition={kickerPosition}
            isLarge={isLarge}
        >
            <React.Fragment>
                {item.loaded ? (
                    <CardHeadline
                        kickerText={item.result.kicker}
                        headlineText={item.result.shortHeadline}
                        hasBackground={hasBackground}
                        hideKicker={hideKicker}
                        largeKicker={largeKicker}
                        fontScale={fontScale}
                        kickerPosition={kickerPosition}
                        isLive={item.result.isLive}
                        showVideoIcon={
                            !hideVideoIcon && isPublicationCardItemWithVideo(item.result)
                        }
                        isSponsored={item.result.isSponsored}
                        publicationKind={item.result.publicationKind}
                        isLarge={isLarge}
                        isPremium={item.result.isPremium && !hidePremiumIcon}
                    />
                ) : (
                    <CardHeadlineSkeleton
                        hasBackground={hasBackground}
                        fontScale={fontScale}
                        isLarge={isLarge}
                    />
                )}
                {showTeaser && (
                    <CardTeaser
                        teaserOrSkeletonLines={item.loaded ? item.result.teaser : 1}
                        hasBackground={hasBackground}
                    />
                )}
                {timestamp !== undefined && item.loaded && item.result.publicationDate && (
                    <StyledTimestamp
                        isLarge={isLarge}
                        relative
                        iconVisible
                        hasBackground={hasBackground}
                        timestamp={getLatestTime(
                            theme,
                            item.result.publicationDate,
                            item.result.lastUpdated
                        )}
                    />
                )}
            </React.Fragment>
        </StyledText>
    )
}
CardText.displayName = 'CardText'
